import { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useLang } from "@/lib/i18n";

// Slugs of categories that should appear in the CONGO service section
const CONGO_SERVICE_SLUGS = new Set([
  "livres-formation",
  "sante-bebe",
  "plomberie",
  "electricite",
  "menage",
  "evenementiel",
  "immobilier",
  "soins-beaute",
  "maison-mobilier",
  "artistique",
  "reparation",
  "transport-livraison",
]);

// Country detection from location/zone text
const detectCountry = (item) => {
  const txt = ((item.zone || "") + " " + (item.location || "")).toLowerCase();
  if (/rdc|congo|kinshasa|lubumbashi|goma|kolwezi|bukavu|matadi/.test(txt)) return "CD";
  if (/france|paris|lyon|marseille|toulouse|nice|bordeaux|nantes|strasbourg|lille/.test(txt)) return "FR";
  return "OTHER";
};

export default function Listing({ kind }) {
  const [sp, setSp] = useSearchParams();
  const { t, tCategory } = useLang();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [countryTab, setCountryTab] = useState(sp.get("pays") || (kind === "service" ? "CD" : "ALL"));
  const [filters, setFilters] = useState({
    q: sp.get("q") || "",
    category_id: sp.get("category_id") || "all",
    min_price: sp.get("min_price") || "",
    max_price: sp.get("max_price") || "",
    city: sp.get("city") || "",
  });
  const [loading, setLoading] = useState(false);
  const endpoint = kind === "service" ? "/services" : "/products";

  useEffect(() => {
    api.get(`/categories?kind=${kind}`).then((r) => setCategories(r.data));
  }, [kind]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.q) params.set("q", filters.q);
    if (filters.category_id && filters.category_id !== "all") params.set("category_id", filters.category_id);
    if (filters.min_price) params.set("min_price", filters.min_price);
    if (filters.max_price) params.set("max_price", filters.max_price);
    if (filters.city) params.set(kind === "service" ? "zone" : "city", filters.city);
    const { data } = await api.get(`${endpoint}?${params}`);
    setItems(data);
    setLoading(false);
  }, [filters.q, filters.category_id, filters.min_price, filters.max_price, filters.city, kind, endpoint]);

  // We deliberately re-run when the URL search params (`sp`) change, since the
  // form state (`filters`) is synced to the URL via `onSearch` — this is what
  // the user perceives as "applying" filters.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [sp, kind]);

  const onSearch = (e) => {
    e.preventDefault();
    const newSp = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") newSp.set(k, v); });
    if (kind === "service" && countryTab !== "ALL") newSp.set("pays", countryTab);
    setSp(newSp);
  };

  const setTab = (v) => {
    setCountryTab(v);
    const newSp = new URLSearchParams(sp);
    if (v === "ALL") newSp.delete("pays"); else newSp.set("pays", v);
    setSp(newSp);
  };

  // Filter items client-side by country tab (services only)
  const visibleItems = useMemo(() => {
    if (kind !== "service" || countryTab === "ALL") return items;
    return items.filter((it) => detectCountry(it) === countryTab);
  }, [items, kind, countryTab]);

  // Filter category dropdown options by country tab for services
  const visibleCategories = useMemo(() => {
    if (kind !== "service" || countryTab === "ALL") return categories;
    if (countryTab === "CD") return categories.filter((c) => CONGO_SERVICE_SLUGS.has(c.slug));
    return categories;
  }, [categories, kind, countryTab]);

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12" data-testid={`${kind}-listing-page`}>
      <div className="mb-6 sm:mb-8">
        <span className="label-uppercase text-xs text-[#C85A32]">Marketplace</span>
        <h1 className="heading font-extrabold text-3xl sm:text-4xl lg:text-5xl mt-2 text-[#111812]">
          {kind === "service" ? t("listing.services.title") : t("listing.products.title")}
        </h1>
      </div>

      {/* Country tabs — only for services */}
      {kind === "service" && (
        <div className="flex flex-wrap gap-2 mb-8 border-b border-[#1A3322]/10" data-testid="country-tabs">
          {[
            { code: "CD", flag: "🇨🇩", label: t("listing.tab.congo") },
            { code: "FR", flag: "🇫🇷", label: t("listing.tab.france") },
            { code: "ALL", flag: "🌍", label: t("common.all") },
          ].map((tab) => (
            <button
              key={tab.code}
              onClick={() => setTab(tab.code)}
              data-testid={`country-tab-${tab.code}`}
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-t-lg font-semibold text-sm border-b-2 -mb-px transition ${
                countryTab === tab.code
                  ? "border-[#C85A32] text-[#1A3322] bg-white"
                  : "border-transparent text-[#4B5563] hover:text-[#1A3322]"
              }`}
            >
              <span className="text-lg">{tab.flag}</span>
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        <aside className="bg-white rounded-lg border border-[#1A3322]/10 p-6 h-fit space-y-5" data-testid="filters-panel">
          <p className="label-uppercase text-xs text-[#1A3322]">{t("listing.filter.search")}</p>
          <form onSubmit={onSearch} className="space-y-4">
            <div className="space-y-2">
              <Label>{t("listing.filter.search")}</Label>
              <Input value={filters.q} onChange={(e) => setFilters({...filters, q: e.target.value})} placeholder={t("listing.filter.keyword")} data-testid="filter-q"/>
            </div>
            <div className="space-y-2">
              <Label>{t("listing.filter.category")}</Label>
              <Select value={filters.category_id} onValueChange={(v) => setFilters({...filters, category_id: v})}>
                <SelectTrigger data-testid="filter-category"><SelectValue placeholder={t("common.all")}/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("common.all")}</SelectItem>
                  {visibleCategories.map((c) => <SelectItem key={c.id} value={c.id}>{tCategory(c)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label>{t("listing.filter.priceMin")}</Label>
                <Input type="number" value={filters.min_price} onChange={(e) => setFilters({...filters, min_price: e.target.value})} data-testid="filter-min-price"/>
              </div>
              <div className="space-y-2">
                <Label>{t("listing.filter.priceMax")}</Label>
                <Input type="number" value={filters.max_price} onChange={(e) => setFilters({...filters, max_price: e.target.value})} data-testid="filter-max-price"/>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{kind === "service" ? "Zone" : t("listing.filter.city")}</Label>
              <Input value={filters.city} onChange={(e) => setFilters({...filters, city: e.target.value})} data-testid="filter-city"/>
            </div>
            <Button type="submit" className="w-full pill-btn bg-[#1A3322] hover:bg-[#24472F] rounded-full" data-testid="filter-apply">{t("common.apply")}</Button>
          </form>
        </aside>

        <div>
          {kind === "service" && countryTab !== "ALL" && (
            <div className="mb-4 flex flex-wrap gap-2" data-testid="category-chips">
              {visibleCategories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    const newSp = new URLSearchParams(sp);
                    if (filters.category_id === c.id) {
                      newSp.delete("category_id");
                      setFilters((f) => ({ ...f, category_id: "all" }));
                    } else {
                      newSp.set("category_id", c.id);
                      setFilters((f) => ({ ...f, category_id: c.id }));
                    }
                    setSp(newSp);
                  }}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${
                    filters.category_id === c.id
                      ? "bg-[#1A3322] text-white border-[#1A3322]"
                      : "bg-white text-[#1A3322] border-[#1A3322]/20 hover:border-[#1A3322]"
                  }`}
                  data-testid={`chip-${c.slug}`}
                >
                  {tCategory(c)}
                </button>
              ))}
            </div>
          )}
          <p className="text-sm text-[#4B5563] mb-4" data-testid="results-count">{visibleItems.length} {t("common.results")}</p>
          {loading ? (
            <p>{t("common.loading")}</p>
          ) : visibleItems.length === 0 ? (
            <p className="text-[#4B5563]">Aucun résultat.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-testid="results-grid">
              {visibleItems.map((p) => <ProductCard key={p.id} p={p} kind={kind}/>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
